import {Injectable, inject} from '@angular/core';
import {MonoTypeOperatorFunction, Observable, of, range, throwError, zip} from 'rxjs';
import {User} from '../../_data/admin/user';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {ConfirmModalService} from '../gui/confirm-modal.service';
import {AddUser, UpdateBranches, UpdateIp, UpdateTime} from '../../_data/admin/add-user';
import {catchError, filter, map, mergeMap, switchMap, take, tap} from 'rxjs/operators';
import {equalsIgnoreCase, getRandomInt} from '../../_utils/lang';
import {first} from '../../_utils/collect';
import {debugFirst, mapErrorToHttpResponse, tapStatusToNotification} from '../../_utils/operators';
import {NotificationService} from '../gui/notification.service';
import {ChangePasswordError} from '../../_data/admin/change-password-error';
import {ApiEndpoints} from '../../_constants/api-endpoints';
import {JWT_TYPE_HEADER} from '../../_constants/constants';

const USER_UPDATED_KEY = 'content.user-updated';

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  private http = inject(HttpClient);
  private modalService = inject(ConfirmModalService);
  private notificationService = inject(NotificationService);
  private apiEndpoints = inject(ApiEndpoints);


  /** rxjs pipe that emits some user-created notifications through the notification service (snackbar) based on the http response */
  private createUserNotification<T>(): MonoTypeOperatorFunction<HttpResponse<T>> {
    return tapStatusToNotification(this.notificationService, [
      {statusCode: 200, i18nMessage: 'content.user-created'},
      {statusCode: 201, i18nMessage: 'content.user-created'}
    ], {i18nMessage: 'content.user-not-created'});
  }

  /** rxjs pipe that emits some user-updated notifications through the notification service (snackbar) based on the http response */
  private updateUserTap(): MonoTypeOperatorFunction<HttpResponse<any>> {
    return tapStatusToNotification(this.notificationService, [
      {statusCode: 200, i18nMessage: USER_UPDATED_KEY},
      {statusCode: 201, i18nMessage: USER_UPDATED_KEY}
    ], {i18nMessage: 'content.user-not-updated'}, true, [400]);
  }

  /**
   * Add a user or edit a user/admin.  Notice: NOT for adding an admin.  Adding an admin requires a separate API endpoint.
   *
   * @see {@link UsersService.addAdmin}
   */
  private addEditUser(addUser: AddUser, addEdit: 'add' | 'edit'): Observable<HttpResponse<string>> {
    addUser.userName = addUser.userName.toUpperCase();
    if(addEdit === 'add') {
      //When adding, we can send all the data at once to the same endpoint.
      return this.http.post(`${this.apiEndpoints.usersAdd}`, addUser, {observe: 'response', responseType: 'text'}).pipe(this.createUserNotification());
    } else {
      /*
       * When editing, we have to hit the APIs separately.
       *  - The updateIp API
       *  - The updateTime API
       *  - The updateBranches API
       *  - The updateUser API
       *
       * So we will set up the API calls that can happen independently.
       * All the data comes into this method neatly wrapped in the AddUser object so we'll unwrap it into separate objects and prepare the requests
       */

      //Prepare the data and request for updating any IP restriction changes
      const updatedIpRestrictions: UpdateIp = {userName: addUser.userName, ipRestrictions: addUser.ipRestrictions};
      const updateIp$ = this.http.post(this.apiEndpoints.usersUpdateIp, updatedIpRestrictions, {observe: 'response', responseType: 'text'});

      //Prepare the data and request for updating any time restriction changes
      const updatedTime: UpdateTime = {userName: addUser.userName, timeRestrictions: addUser.timeRestrictions};
      const updateTime$ = this.http.post(this.apiEndpoints.usersUpdateAccessTimes, updatedTime, {observe: 'response', responseType: 'text'});

      //Prepare the data and request for updating any branch changes
      const updatedBranches: UpdateBranches = {userName: addUser.userName, branchNames: addUser.branches};
      const updateBranches$ = this.http.post(this.apiEndpoints.usersUpdateBranches, updatedBranches, {observe: 'response', responseType: 'text'});

      //Blank out the data that we unwrapped.  The updateUser API doesn't need this data
      addUser.ipRestrictions = [];
      addUser.timeRestrictions = [];
      addUser.branches = [];

      //Prepare the final request for updating any user info
      const updateUser$ = this.http.post(`${this.apiEndpoints.usersUpdate}`, addUser, {observe: 'response', responseType: 'text'}).pipe(this.updateUserTap());

      //Zip the preliminary requests together, let them run asynchronously, and then when they're all done we will run the final updateUser request
      return zip(updateIp$, updateTime$, updateBranches$).pipe(mergeMap(() => updateUser$));
    }
  }

  /**
   * Adds a user of Admin type.
   *
   * @param addUser The information needed for creating the admin
   * @private
   */
  private addAdmin(addUser: AddUser): Observable<HttpResponse<string>> {
    addUser.userName = addUser.userName.toUpperCase();
    return this.http.post(`${this.apiEndpoints.adminsAdd}`, addUser, {observe: 'response', responseType: 'text'}).pipe(this.createUserNotification());
  }

  /** Enable or disable a user */
  private enableDisableUser(endpoint: string, userName: string): Observable<HttpResponse<string>> {
    return this.http.post(endpoint, {userName: userName}, {observe: 'response', responseType: 'text'}).pipe(this.updateUserTap());
  }

  /**
   * @returns {Observable}. If the modal was accepted: HttpResponse, if not: null
   */
  private confirmModifyUser(
    user: User,
    title: {i18nKey: string; data?: Record<string, unknown>},
    body: {i18nKey: string; data?: Record<string, unknown>},
    modifyUserObs: (userName: string) => Observable<HttpResponse<string> | null>,
    ariaLabelledBy: string,
    container: HTMLElement
  ): Observable<HttpResponse<string>> {
    return this.modalService.open(title, body, ariaLabelledBy, container).result$.pipe(
      switchMap(accepted => {
        if(accepted) {
          console.debug(`OK clicked.`);
          return modifyUserObs(user.userName);
        } else {
          return of(null);
        }
      })
    );
  }

  /** This wrapper handles the common rxjs logic when changing the password from one of two ways (by token or not) */
  private changePasswordPipe(changePassword$: Observable<HttpResponse<string>>): Observable<HttpResponse<string | ChangePasswordError>> {
    return changePassword$.pipe(
      mapErrorToHttpResponse(this.notificationService, [{statusCode: 412}, {statusCode: 400}]),
      tap((response: HttpResponse<string>) => console.debug(`Reset password response: ${response.body}`)),
      //Override the default snackbar message when it's an unexpected status since we'll handle those
      this.updateUserTap(),
      map(response => {
        //This endpoint spits back json when there's a 400
        if(response.status === 400 && response.headers.get('Content-Type').startsWith('application/json')) {
          return new HttpResponse({status: response.status, body: JSON.parse(response.body)});
        } else {
          return response;
        }
      })
    );
  }

  /** Get the user details object for a given username.  Similar to {@link getUser} but uses a different API */
  getUserDetails(userName: string): Observable<HttpResponse<User>> {
    return this.http.post<User>(this.apiEndpoints.userGetDetails, {userName: userName}, {observe: 'response'}).pipe(tap(response => {
      if(response.status === 204) {
        console.warn('User has no details');
      }
    }));
  }

  /**
   * Will take either a username, or a User object, and give back the appropriate user object.
   *
   * If a User object is provided, it's simply passed back.
   *
   * If a username is provided, the getUserDetails API is called to convert it to the User object.
   * If an http error occurs, a null is returned.
   */
  getUserDetailsQuietly(user: User | string): Observable<User> {
    if(typeof user === 'string') {
      return this.getUserDetails(user).pipe(
        catchError(() => of(null)),
        map(response => response?.body)
      );
    } else {
      return of(user);
    }
  }

  /** Get the user object for a given username.  Similar to {@link getUserDetails} but uses a different API */
  getUser(userName: string): Observable<HttpResponse<User>> {
    return debugFirst(
      `Getting user by userName ${userName}...`,
      this.http.post<User[]>(this.apiEndpoints.usersGet, {userName: userName}, {observe: 'response'}).pipe(
        map(response => response.clone({body: first(response.body)})))
    );
  }

  /** Get a list of users from the API for a given search term */
  searchForUsers(userName: string): Observable<User[]> {
    console.debug(`Search for users by "${userName}"`);
    return this.http.post<User[]>(this.apiEndpoints.usersSearch, {userName: userName});
  }

  /** Depending on the userType, will add/update a user or add an admin (which all use the same object but require different APIs) */
  addUser(addUser: AddUser, userType: 'user' | 'admin' = 'user'): Observable<boolean> {
    const obs = userType === 'user' && this.addEditUser(addUser, 'add') || this.addAdmin(addUser);
    return obs.pipe(map(value => value.status === 200 || value.status === 201));
  }

  /**
   * Edit a user or admin
   *
   * @returns {Observable} for the Http status code.  200/201 = success. 304 = not updated.
   */
  editUser(addUser: AddUser): Observable<boolean> {
    return this.addEditUser(addUser, 'edit').pipe(map(value => {
      const success = value.status === 200 || value.status === 201;
      if(!success) {
        console.warn(`Add user failed.  Status: ${value.status}, body: ${value.body}`);
      }
      return success;
    }));
  }

  /**
   * Check if a username is available for use.
   *
   * @param userName The username to check
   */
  userNameValid(userName: string): Observable<boolean> {
    return this.http.post(this.apiEndpoints.userNameExists, {userName: userName}, {observe: 'response', responseType: 'text'})
      .pipe(
        map(value => {
          const userNameValid = value.status === 200 && equalsIgnoreCase('username is valid', value.body);
          if(!userNameValid) {
            console.debug(`UserName not valid.  Status: ${value.status}, body: ${value.body}`);
          }
          return userNameValid;
        })
      );
  }

  /**
   * Find a free variant of a base username.  First checks if the provided basename is free.  Eg: Is "USERNAME" free?
   * If it isn't, check again with 3 random numbers appended.  Eg: Is "USERNAME116" free?
   *
   * Repeat up to three times to find a valid username variant and return the variant in an observable.
   */
  validUserNameVariant(userName: string): Observable<string> {
    //TODO: (bug) what happens if this can't produce a valid username?
    return range(0, 3).pipe(
      mergeMap(value => {
        const userNameToCheck = userName + (value === 0 ? '' : String(getRandomInt(1, 1000)));
        return this.userNameValid(userNameToCheck).pipe(
          tap(valid => console.debug(`${userNameToCheck} valid? ${valid}`)),
          map(valid => ({u: userNameToCheck, v: valid}))
        );
      }),
      filter(uv => uv.v),
      map(uv => uv.u),
      take(1)
    );
  }

  /** Disable a user by their username */
  disableUser(userName: string): Observable<HttpResponse<string>> {
    return this.enableDisableUser(this.apiEndpoints.userDisable, userName);
  }

  /** Enable a user by their username */
  enableUser(userName: string): Observable<HttpResponse<string>> {
    return this.enableDisableUser(this.apiEndpoints.userEnable, userName);
  }

  /** Terminate a user by their username */
  terminateUser(userName: string): Observable<HttpResponse<string>> {
    return this.http.delete(this.apiEndpoints.userDelete, {observe: 'response', responseType: 'text', body: {userName: userName}});
  }

  /** Reset a user's password by their username */
  resetPassword(userName: string): Observable<HttpResponse<string>> {
    return this.http.post(this.apiEndpoints.userResetPassword, {userName: userName}, {observe: 'response', responseType: 'text'}).pipe(
      tap((response: HttpResponse<string>) => console.debug(`Reset password response: ${response.body}`)),
      this.updateUserTap()
    );
  }

  /** Perform the change password API request for when the user knows their current password */
  changePassword(userName: string, oldPassword: string, newPassword: string): Observable<HttpResponse<string | ChangePasswordError>> {
    console.debug('Creating change password observable');
    return this.changePasswordPipe(
      this.http.post(
        this.apiEndpoints.userChangePassword,
        {userName: userName, oldPassword: oldPassword, newPassword: newPassword},
        {headers: {[JWT_TYPE_HEADER]: 'auth'}, observe: 'response', responseType: 'text'}
      )
    );
  }

  /** Perform the change password API request for when the user doesn't know their current password, and is using the forgot-password email */
  resetPasswordByToken(password: string, token: string): Observable<HttpResponse<string | ChangePasswordError>> {
    return this.changePasswordPipe(
      this.http.post(`${this.apiEndpoints.userResetPasswordByToken}`, {password: password, token: token}, {observe: 'response', responseType: 'text'})
        .pipe(mapErrorToHttpResponse(this.notificationService,
          [{statusCode: 406, i18nMessage: 'content.invalid-token'},{statusCode: 409, i18nMessage: 'content.recently.used.password'}]))
    );
  }

  /** Ask the API to send the user a forgot-password email */
  sendToken(email: string): Observable<boolean> {
    return this.http.post(this.apiEndpoints.userSendToken, {userEmail: email}, {observe: 'response', responseType: 'text'})
      .pipe(
        catchError(err => {
          this.notificationService.displayI18n('content.error-occurred');
          return throwError(err);
        }),
        tapStatusToNotification(this.notificationService, [{statusCode: 200, i18nMessage: 'content.forgot-password-email-requested'}]),
        map(response => response.status === 200)
      );
  }

  /** Show a confirmation box, asking the user to confirm disabling the other user (and perform the action if they confirm) */
  confirmDisableUser(user: User, ariaLabelledBy: string, container: HTMLElement): Observable<HttpResponse<string> | null> {
    const name = this.getUserDisplayName(user);
    return this.confirmModifyUser(
      user,
      {i18nKey: 'content.disable-something', data: {what: name}},
      {i18nKey: 'content.confirm-disable-something', data: {what: name}},
      u => this.disableUser(u),
      ariaLabelledBy,
      container
    ).pipe(tapStatusToNotification(this.notificationService, [{statusCode: 200, i18nMessage: USER_UPDATED_KEY}]));
  }

  /** Show a confirmation box, asking the user to confirm enabling the other user (and perform the action if they confirm) */
  confirmEnableUser(user: User, ariaLabelledBy: string, container: HTMLElement): Observable<HttpResponse<string> | null> {
    const name = this.getUserDisplayName(user);
    return this.confirmModifyUser(
      user,
      {i18nKey: 'content.enable-something', data: {what: name}},
      {i18nKey: 'content.confirm-enable-something', data: {what: name}},
      u => this.enableUser(u),
      ariaLabelledBy,
      container
    ).pipe(tapStatusToNotification(this.notificationService, [{statusCode: 200, i18nMessage: USER_UPDATED_KEY}]));
  }

  /** Show a confirmation box, asking the user to confirm resetting the password of the other user (and perform the action if they confirm) */
  confirmResetUserPassword(user: User, ariaLabelledBy: string, container: HTMLElement): Observable<HttpResponse<string> | null> {
    const name = this.getUserDisplayName(user);
    return this.confirmModifyUser(
      user,
      {i18nKey: 'content.reset-password-for', data: {who: name}},
      {i18nKey: 'content.confirm-reset-password', data: {who: name}},
      u => this.resetPassword(u),
      ariaLabelledBy,
      container
    ).pipe(tapStatusToNotification(this.notificationService, [{statusCode: 200, i18nMessage: USER_UPDATED_KEY}]));
  }

  /** Show a confirmation box, asking the user to confirm terminating the other user (and perform the action if they confirm) */
  confirmTerminateUser(user: User, ariaLabelledBy: string, container: HTMLElement): Observable<boolean> {
    const name = this.getUserDisplayName(user);
    return this.confirmModifyUser(
      user,
      {i18nKey: 'content.terminate-something', data: {what: name}},
      {i18nKey: 'content.confirm-terminate-something', data: {what: name}},
      u => this.terminateUser(u),
      ariaLabelledBy,
      container
    ).pipe(
      mapErrorToHttpResponse(this.notificationService, [{statusCode: 405, i18nMessage: 'content.error-occurred'}]),
      tapStatusToNotification(this.notificationService, [{statusCode: 200, i18nMessage: 'content.user-terminated'}]),
      map(response => response.status === 200)
    );
  }

  /** Convenience method to provide the first and last name as a string */
  getUserDisplayName(user: User): string {
    return `${user.firstName} ${user.lastName}`;
  }

  /** Convenience method to figure out if a user object is disabled/enabled */
  isUserEnabled(user: User): boolean {
    return !this.isUserDisabled(user);
  }

  /** Convenience method to figure out if a user object is disabled/enabled */
  isUserDisabled(user: User) {
    return equalsIgnoreCase('DISABLED', user.status);
  }
}
